import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';
import EventPublisher from '../../utilities/event_publisher';
import SvgValidator from '../../utilities/layouts/svg_validator';
import { flashAlert, flashNotice } from '../../shared/flash';
import { maxBy, find, uniqBy, sortBy } from 'lodash';
import Landmark from '../../models/landmark';

export default class extends Controller {
  static targets = [
    'landmarkList',
    'fileInput',
    'uploadBtn',
    'landmarkLabel',
    'container',
    'settingsContainer',
    'listItem',
    'label',
    'landmarkType'
  ];

  connect() {
    window.addEventListener('landmarks-loaded', this.onLoadLandmarks);
    window.addEventListener('landmark-added', this.onLandmarkAdded);
    window.addEventListener('landmark-selected', this.onLandmarkSelected);
    window.addEventListener('selection-cleared', this.onSelectionCleared);
    this.fileInputTarget.addEventListener('change', this.onFileChanged);
  }

  onLoadLandmarks = event => {
    this.landmarks = event.detail.landmarks;
    this.renderLandmarksList();
  }

  onLandmarkAdded = event => {
    this.onLandmarkSelected(event);
    this.renderLandmarksList();
  }

  onLandmarkSelected = event => {
    if (!event.detail.landmark) return;
    this.landmark = event.detail.landmark;
    this.landmark.setActive();
    this.landmarkLabelTarget.value = this.landmark.label;
    this.landmarkTypeTarget.value = this.landmark.filename;
    this.settingsContainerTarget.classList.remove('hidden')
    this.containerTarget.classList.remove('hidden')
  }

  onSelectionCleared = () => {
    this.settingsContainerTarget.classList.add('hidden');
  }

  handleLabelChanged(event) {
    this.landmark.setLabel(event.target.value);
  }

  handleLandmarkSelected(event) {
    const { id } = event.target.dataset;
    const landmark = find(this.landmarks, { id: parseInt(id) });
    const maxID = maxBy(this.landmarks, 'id')?.id || 0;
    const clonedLandmark = landmark.clone();
    clonedLandmark.id = maxID + 1;
    EventPublisher.publish('landmark-added', { landmark: clonedLandmark });
  }

  handleLandmarkRemoved() {
    const confirmed = confirm(`Are you sure you want to remove ${this.landmark.label} Landmark?`); // eslint-disable-line no-alert
    if (!confirmed) return;
    const listItem = this.getListItem();
    EventPublisher.publish('landmark-removed', { landmark: this.landmark });
    this.landmarkLabelTarget.value = '';
    this.landmark.remove();
    listItem.remove();
    this.renderLandmarksList();
    this.settingsContainerTarget.classList.add('hidden');
  }

  onFileChanged = () => {
    const file = this.fileInputTarget.files[0];
    const svgValidator = new SvgValidator(file);
    svgValidator
      .validate()
      .then(() => this.handleValidFile(file))
      .catch(() => this.handleInvalidFile(file));
  }

  handleValidFile(file) {
    const url = this.fileInputTarget.dataset.directUploadUrl;
    const upload = new DirectUpload(file, url);
    this.uploadBtnTarget.innerHTML = 'Uploading...';
    upload.create((error, blob) => {
      this.uploadBtnTarget.innerHTML = 'Upload New Landmark';
      if (error) this.handleUploadFailure(error);
      else this.handleUploadSuccess(blob);
    });
  }

  handleInvalidFile(file) {
    flashAlert(`${file.name} is incompatible. We can't handle an opacity value. Please use RGBA values instead.`);
  }

  handleUploadFailure(error) {
    console.error(error);
    flashAlert("Oops! couldn't upload file. Try again later");
  }

  handleUploadSuccess(blob) {
    const bucket = this.fileInputTarget.dataset.directUploadBucket;
    const svgURL = `https://${bucket}.s3.amazonaws.com/${blob.key}`;
    // assign a unique identifier to the landmark
    const id = (maxBy(this.landmarks, 'id')?.id || 0) + 1;
    const landmark = new Landmark(id, 0, 1, 1, 0, 0, svgURL, `Untitled-${id}`, blob.filename);
    EventPublisher.publish('landmark-added', { landmark });
    EventPublisher.publish('landmark-selected', { landmark });
    flashNotice(`Uploaded ${blob.filename} successfully!`);
  }

  openFileDialog() {
    this.fileInputTarget.click();
  }

  getListItem() {
    return find(this.listItemTargets, { id: `listItem-${this.landmark.filename}` });
  }

  renderLandmarksList() {
    this.landmarkListTarget.innerHTML = '';
    const uniqLandmarksByFileName = sortBy(uniqBy(this.landmarks, 'filename'), 'filename');
    for (let landmark of uniqLandmarksByFileName) {
      this.landmarkListTarget.innerHTML += this.renderLandmark(landmark);
    }
  }

  renderLandmark(landmark) {
    return `
      <li class='mb-4 border-b cursor-pointer relative py-2'
          data-templated-layouts--landmark-settings-target='listItem'
          id='listItem-${landmark.filename}'
          data-id='${landmark.id}'
          data-action='click->templated-layouts--landmark-settings#handleLandmarkSelected'>
        <span data-id='${landmark.id}' class='text-md'>${landmark.filename}</span>
      </li>
    `;
  }
}
